import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HxButton } from '../../button/button';
import { HxIcon } from '../../icon/icon';
import type { HxJoditVariable } from '../models/variable.types';

/**
 * Side panel component for displaying and selecting template variables.
 *
 * Displays variables as full-width buttons in a vertical panel. Supports search/filtering. Emits variableSelected when
 * a variable button is clicked.
 *
 * @example
 *   ```html
 *   <hx-variable-side-panel
 *     [variables]="availableVariables"
 *     (variableSelected)="onVariableSelect($event)" />
 *   ```;
 */
@Component({
  selector: 'hx-variable-side-panel',
  standalone: true,
  imports: [FormsModule, HxButton, HxIcon],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <aside aria-label="Template Variables" class="hx-variable-side-panel" role="complementary">
      <!-- Header -->
      <header class="hx-variable-side-panel__header">
        <h3 class="hx-variable-side-panel__title">Variables</h3>
      </header>

      <!-- Search -->
      <div class="hx-variable-side-panel__search">
        <input
          aria-label="Search variables"
          class="hx-variable-side-panel__search-input"
          placeholder="Search variables..."
          type="text"
          [ngModel]="searchTerm()"
          (ngModelChange)="searchTerm.set($event)" />
        @if (searchTerm()) {
          <button
            aria-label="Clear search"
            class="hx-variable-side-panel__search-clear"
            type="button"
            (click)="searchTerm.set('')">
            <hx-icon inline svgIcon="x" />
          </button>
        }
      </div>

      <!-- Variables list -->
      <div class="hx-variable-side-panel__content">
        @if (filteredVariables().length > 0) {
          <div aria-label="Available variables" class="hx-variable-side-panel__chips" role="group">
            @for (variable of filteredVariables(); track variable.id) {
              <button
                align="start"
                context="neutral"
                hxButton
                size="sm"
                type="button"
                variant="soft"
                [attr.aria-label]="'Insert ' + variable.label + ' variable'"
                [attr.title]="getChipTooltip(variable)"
                [fullWidth]="true"
                (click)="onChipClick(variable)">
                {{ variable.label }}
              </button>
            }
          </div>
        } @else {
          <p class="hx-variable-side-panel__empty">
            @if (searchTerm()) {
              No variables match "{{ searchTerm() }}"
            } @else {
              No variables available
            }
          </p>
        }
      </div>

      <!-- Footer hint -->
      <footer class="hx-variable-side-panel__footer">
        <span class="hx-variable-side-panel__hint">Click a variable to insert at cursor</span>
      </footer>
    </aside>
  `,
  styleUrl: './variable-side-panel.css',
})
export class HxVariableSidePanel {
  // =========================================================================
  // Inputs
  // =========================================================================

  /** Array of available variables to display */
  readonly variables = input.required<HxJoditVariable[]>();

  /** Panel title (default: "Variables") */
  readonly title = input<string>('Variables');

  // =========================================================================
  // Outputs
  // =========================================================================

  /** Emitted when a variable button is clicked */
  readonly variableSelected = output<HxJoditVariable>();

  // =========================================================================
  // State
  // =========================================================================

  /** Current search/filter term */
  readonly searchTerm = signal<string>('');

  // =========================================================================
  // Computed
  // =========================================================================

  /** Filtered variables based on search term */
  readonly filteredVariables = computed(() => {
    const term = this.searchTerm().toLowerCase().trim();
    const vars = this.variables();

    if (!term) {
      return vars;
    }

    return vars.filter(
      (v) =>
        v.label.toLowerCase().includes(term) ||
        v.id.toLowerCase().includes(term) ||
        v.category?.toLowerCase().includes(term) ||
        v.description?.toLowerCase().includes(term)
    );
  });

  // =========================================================================
  // Methods
  // =========================================================================

  /** Handle variable button click - emit the selected variable */
  onChipClick(variable: HxJoditVariable): void {
    this.variableSelected.emit(variable);
  }

  /** Generate tooltip text for a chip */
  getChipTooltip(variable: HxJoditVariable): string {
    const parts: string[] = [`{{${variable.id}}}`];

    if (variable.category) {
      parts.push(`Category: ${variable.category}`);
    }

    if (variable.description) {
      parts.push(variable.description);
    }

    if (variable.example) {
      parts.push(`Example: ${variable.example}`);
    }

    return parts.join('\n');
  }
}
